import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Tooltip,
  Divider,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { NAV_GROUPS } from '../lib/navigation';
import logo6 from './logo6.png';
import logo7 from './logo7.png';

// Keep this in sync with any component (e.g. Navbar) that positions itself
// relative to the sidebar width — a mismatch here is what causes the
// sidebar/navbar to look misaligned.
export const SIDEBAR_WIDTH = 255;
export const SIDEBAR_WIDTH_COLLAPSED = 76;
export const SIDEBAR_HEADER_HEIGHT = 80;

const Sidebar = ({ isCollapsed, toggleSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const drawerWidth = isCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;
  const isDark = theme.palette.mode === 'dark';
  const logo = isDark ? logo6 : logo7;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          transition: theme => theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          backgroundColor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          height: `${SIDEBAR_HEADER_HEIGHT}px`,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          px: isCollapsed ? 2 : 3,
          borderBottom: '1px solid',
          borderColor: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.08)'
            : 'rgba(0, 0, 0, 0.08)',
        }}
      >
        <img
          src={logo}
          alt="Lift Logo"
          style={{
            height: isCollapsed ? '36px' : '48px',
            width: 'auto',
            transition: 'height 0.3s ease',
          }}
        />
      </Box>

      {/* Scrollable nav area - driven entirely by NAV_GROUPS in lib/navigation.js.
          Add a new app's routes there; this component never needs to change. */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 1,
        }}
      >
        {NAV_GROUPS.map((group, groupIndex) => (
          <Box key={group.label || `group-${groupIndex}`}>
            {!isCollapsed && group.label && (
              <Typography
                sx={{
                  px: 2.5,
                  pt: groupIndex === 0 ? 1 : 2,
                  pb: 0.5,
                  fontSize: '12.5px',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  // Section headings (Payments, Groups, Diary, ...) — same
                  // blue used for the active nav item and the navbar icons,
                  // so they read as the app's accent rather than plain
                  // secondary text.
                  color: theme.palette.mode === 'dark' ? '#64b5f6' : '#1976d2',
                }}
              >
                {group.label}
              </Typography>
            )}
            <List sx={{ py: 0 }}>
              {group.items.map((item, index) => {
                const isActive = location.pathname === item.path;
                return (
                  <Box key={item.label}>
                    <Tooltip
                      title={isCollapsed ? item.label : ''}
                      placement="right"
                    >
                      <ListItem
                        button
                        onClick={() => navigate(item.path)}
                        sx={{
                          minHeight: 44,
                          mx: 1,
                          width: 'auto',
                          borderRadius: 1.5,
                          px: isCollapsed ? 1 : 1.75,
                          py: 1,
                          position: 'relative',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                          ...(isActive && {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          }),
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: isCollapsed ? 0 : 1.5,
                            justifyContent: 'center',
                            color: isActive ? '#1976d2' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'inherit'),
                            '& .MuiSvgIcon-root': {
                              fontSize: isCollapsed ? 23 : 21,
                              transition: 'font-size 0.3s ease',
                            }
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        {!isCollapsed && (
                          <ListItemText
                            primary={item.label}
                            sx={{
                              '& .MuiTypography-root': {
                                color: isActive ? '#1976d2' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'text.primary'),
                                fontSize: '0.95rem',
                                fontWeight: isActive ? 600 : 500,
                              },
                            }}
                          />
                        )}
                      </ListItem>
                    </Tooltip>
                    {index < group.items.length - 1 && (
                      <Divider sx={{ opacity: 0.4, mx: 2 }} />
                    )}
                  </Box>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* Fixed footer - always docked to the bottom of the sidebar */}
      <Box
        sx={{
          flexShrink: 0,
          borderTop: '1px solid',
          borderColor: 'divider',
          p: 1,
        }}
      >
        <Box
          component="button"
          onClick={toggleSidebar}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            gap: 1.5,
            width: '100%',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            borderRadius: 1.5,
            px: isCollapsed ? 1 : 1.75,
            py: 1,
            color: '#1976d2',
            fontFamily: 'inherit',
            fontSize: '0.95rem',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          {isCollapsed ? <ChevronRightIcon sx={{ fontSize: 20 }} /> : <ChevronLeftIcon sx={{ fontSize: 20 }} />}
          {!isCollapsed && <span>Collapse</span>}
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;